import { call, put, takeEvery, takeLatest, select } from 'redux-saga/effects';
import { validateToken } from '../tokenValidation/actions';
import { 
  ROUTE_ACCESS_REQUEST,
  ROUTE_ACCESS_GRANTED,
  ROUTE_ACCESS_DENIED,
  REDIRECT_TO_LOGIN
} from './actions';

// Public routes that don't require authentication
const PUBLIC_ROUTES = [
  '/login',
  '/forgot-password',
  '/register',
  '/pages-404',
  '/pages-500',
  '/pages-maintenance',
  '/pages-comingsoon'
];

/**
 * Checks if a route is public (doesn't require authentication)
 * @param {string} pathname - The route path
 * @returns {boolean} - True if route is public
 */
function isPublicRoute(pathname) {
  return PUBLIC_ROUTES.some(route => pathname.startsWith(route));
}

/**
 * Handles route access requests and validates authentication
 * @param {Object} action - Redux action with route information
 */
function* handleRouteAccessRequest(action) {
  try {
    const { pathname, navigate } = action.payload;

    // Check if route is public
    if (isPublicRoute(pathname)) {
      yield put({
        type: ROUTE_ACCESS_GRANTED,
        payload: { pathname, reason: 'public_route' }
      });
      return;
    }

    // For protected routes, check authentication
    const authUserData = localStorage.getItem('authUser');
    
    if (!authUserData) {
      yield put({
        type: ROUTE_ACCESS_DENIED,
        payload: { 
          pathname, 
          reason: 'no_auth_data',
          error: 'No authentication data found' 
        }
      });
      return;
    }

    const authUser = JSON.parse(authUserData);
    const token = authUser.token;

    if (!token) {
      yield put({
        type: ROUTE_ACCESS_DENIED,
        payload: { 
          pathname, 
          reason: 'no_token',
          error: 'No token found in authentication data' 
        }
      });
      return;
    }

    // Validate the token
    yield put(validateToken(token));

    // Wait for token validation result
    const tokenValidationResult = yield take(['TOKEN_VALID', 'TOKEN_INVALID', 'TOKEN_EXPIRING']);

    if (tokenValidationResult.type === 'TOKEN_VALID') {
      yield put({
        type: ROUTE_ACCESS_GRANTED,
        payload: { 
          pathname, 
          reason: 'valid_token',
          token: tokenValidationResult.payload.token 
        }
      });
    } else if (tokenValidationResult.type === 'TOKEN_EXPIRING') {
      // Allow access but show warning
      yield put({
        type: ROUTE_ACCESS_GRANTED,
        payload: { 
          pathname, 
          reason: 'expiring_token',
          token: tokenValidationResult.payload.token,
          timeRemaining: tokenValidationResult.payload.timeRemaining
        }
      });
    } else {
      // Token is invalid
      yield put({
        type: ROUTE_ACCESS_DENIED,
        payload: { 
          pathname, 
          reason: 'invalid_token',
          error: tokenValidationResult.payload.error 
        }
      });
    }

  } catch (error) {
    console.error('Route access validation error:', error);
    yield put({
      type: ROUTE_ACCESS_DENIED,
      payload: { 
        pathname: action.payload.pathname, 
        reason: 'validation_error',
        error: 'Error validating route access' 
      }
    });
  }
}

/**
 * Handles redirect to login when access is denied
 * @param {Object} action - Redux action with redirect information
 */
function* handleRedirectToLogin(action) {
  try {
    const { pathname, navigate } = action.payload;
    
    // Clear any existing authentication data
    localStorage.removeItem('authUser');
    
    // Navigate to login with current path as redirect
    if (navigate) {
      navigate('/login', { 
        state: { from: pathname },
        replace: true 
      });
    }

    yield put({
      type: 'REDIRECT_COMPLETED',
      payload: { redirectedTo: '/login', from: pathname }
    });

  } catch (error) {
    console.error('Redirect to login error:', error);
  }
}

/**
 * Main route validation saga watcher
 */
export function* routeValidationSaga() {
  yield takeEvery(ROUTE_ACCESS_REQUEST, handleRouteAccessRequest);
  yield takeEvery(REDIRECT_TO_LOGIN, handleRedirectToLogin);
}

// Export individual sagas for testing
export { handleRouteAccessRequest, handleRedirectToLogin };
